import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '@/shared/lib';
import { useAuth, type User } from '@/shared/contexts/AuthContext';

/**
 * Public invite acceptance page.
 * Exchanges association access_token for JWT session, stores auth in context,
 * then opens the restaurant order parser (name + location dropdown).
 */
const InviteAcceptPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';
  const { establishSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    token ? 'loading' : 'error'
  );

  useEffect(() => {
    if (!token) {
      setError('Missing invite token.');
      setStatus('error');
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const response = await authApi.post('/api/auth/verify-association-token', {
          token
        });
        const accessToken =
          response.data?.accessToken ??
          response.data?.access_token ??
          response.data?.data?.accessToken ??
          response.data?.data?.access_token;
        const refreshToken =
          response.data?.refreshToken ??
          response.data?.refresh_token ??
          response.data?.data?.refreshToken ??
          null;
        const user = (response.data?.user ??
          response.data?.data?.user) as User | undefined;

        if (cancelled) return;

        if (!accessToken || !user?.id) {
          setError('Invite accepted but no session was returned.');
          setStatus('error');
          return;
        }

        establishSession({
          accessToken: String(accessToken),
          refreshToken: refreshToken ? String(refreshToken) : null,
          user
        });

        setStatus('success');
        // Clean URL — session is in AuthContext / localStorage.
        window.location.replace('/order-parser');
      } catch (err: unknown) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'This invite link is invalid or expired.';
        setError(message);
        setStatus('error');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, establishSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
      <div className="max-w-md w-full rounded-2xl bg-background-card shadow-sm border border-border-primary p-8 text-center">
        {status === 'loading' && (
          <>
            <h1 className="text-xl font-semibold text-[#006B82] mb-2">
              Accepting invite…
            </h1>
            <p className="text-text-secondary text-sm">Please wait a moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <h1 className="text-xl font-semibold text-[#006B82] mb-2">
              Invite accepted
            </h1>
            <p className="text-text-secondary text-sm">Opening order page…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Could not accept invite
            </h1>
            <p className="text-text-secondary text-sm mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-block rounded-lg bg-[#006B82] text-white px-4 py-2 text-sm font-medium"
            >
              Go to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
